import { Box, Tooltip, Typography } from '@mui/material';
import { Clock, File, Star, Users } from 'lucide-react';
import React from 'react';
import { useAppMode } from '../../hooks/useAppMode';
import SyncStatusBadge from './SyncStatusBadge';

const STAR_ACTIVE = '#F5B400';
const STAR_INACTIVE = '#A9B6BC';

const StarToggle = ({ isStarred, onClick, size = 18 }) => (
  <Tooltip title={isStarred ? 'Unstar' : 'Star'}>
    <Box
      component="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      aria-label={isStarred ? 'Unstar project' : 'Star project'}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 28,
        height: 28,
        p: 0,
        border: 'none',
        borderRadius: '50%',
        bgcolor: 'transparent',
        cursor: 'pointer',
        color: isStarred ? STAR_ACTIVE : STAR_INACTIVE,
        transition: 'background-color 0.15s, color 0.15s',
        '&:hover': { bgcolor: 'rgba(0,0,0,0.06)', color: STAR_ACTIVE },
      }}
    >
      <Star size={size} strokeWidth={1.8} fill={isStarred ? STAR_ACTIVE : 'none'} />
    </Box>
  </Tooltip>
);

const ProjectCard = ({ project, onClick, viewMode = 'grid', isStarred = false, onToggleStar }) => {
  const isList = viewMode === 'list';
  const { canShowDirtyBadge } = useAppMode();
  const handleToggleStar = () => onToggleStar?.(project.project_id);

  return (
    <Box
      onClick={() => onClick(project)}
      sx={{
        display: 'flex',
        flexDirection: isList ? 'row' : 'column',
        gap: isList ? 2 : 1,
        p: 2,
        borderRadius: '12px',
        bgcolor: '#f1f2f2',
        boxShadow: '0 -1px 0 0 rgba(0, 0, 0, 0.10), 0 1px 0 0 #FFF',
        cursor: 'pointer',
        transition: 'all 0.15s',
        minHeight: isList ? 'auto' : 160,
        position: 'relative',
        '&:hover': {
          bgcolor: '#e8eaec',
          boxShadow: '0 -1px 0 0 rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0,0,0,0.08)',
        },
      }}
    >
      {/* Star — grid: top-right corner overlay */}
      {!isList && (
        <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
          <StarToggle isStarred={isStarred} onClick={handleToggleStar} />
        </Box>
      )}

      {/* Title + dirty badge */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: !isList ? 4 : 0 }}>
        <Typography sx={{ fontWeight: 600, color: '#394b57', fontSize: 16 }}>
          {project.name}
        </Typography>
        {canShowDirtyBadge && <SyncStatusBadge isDirty={project.is_dirty} />}
      </Box>

      {/* Description */}
      {!isList && project.description && (
        <Typography
          sx={{
            color: '#7d8d97',
            fontSize: 12,
            lineHeight: 1.4,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {project.description}
        </Typography>
      )}

      {/* Meta row */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mt: isList ? 0 : 'auto',
          alignItems: 'center',
          ml: isList ? 'auto' : 0,
        }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#7d8d97', fontSize: 11 }}
        >
          <File size={12} strokeWidth={1.5} />
          <span>{project.file_count ?? 0} files</span>
        </Box>
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#7d8d97', fontSize: 11 }}
        >
          <Clock size={12} strokeWidth={1.5} />
          <span>
            {new Date(project.created_at).toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
            })}
          </span>
        </Box>
      </Box>

      {/* Star — list: end of row */}
      {isList && (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <StarToggle isStarred={isStarred} onClick={handleToggleStar} size={16} />
        </Box>
      )}

      {/* Footer: members + creator */}
      {!isList && (
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}
        >
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#7d8d97', fontSize: 11 }}
          >
            <Users size={12} strokeWidth={1.5} />
            <span>
              {project.member_count ?? 1} member{(project.member_count ?? 1) !== 1 ? 's' : ''}
            </span>
          </Box>
          {(project.creator_display_name || project.creator_username) && (
            <Typography sx={{ color: '#A9B6BC', fontSize: 11 }}>
              {project.creator_display_name || project.creator_username}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ProjectCard;
